// stepThreeValidation.js
import * as Yup from 'yup';
import { isPastDate, isFutureDate, isAtLeast18 } from './validationHelpers';


const isLessThan2Years = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (isNaN(date)) return false;
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
  return date > twoYearsAgo;
};


export const stepThreeSchema = Yup.object().shape({
  employment_status: Yup.string()
    .required('Employment Status is required')
    .oneOf(['full-time', 'part-time','self-employed', 'un-employed', 'retired'], 'Invalid Employment Status selected'),
  employer: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) => schema.required("Employer's Name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  job_title: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed',].includes(val),
    then: (schema) => schema.required('Job Title is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  employer_phone_1: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) =>
      schema
        .required("Employer's Phone Number is required")
        .test('is-valid-phone', "Phone number must be 10 digits", val =>
          val && val.replace(/[^\d]/g, '').length === 10
        )
        .matches(/^\d{3}-\d{3}-\d{4}$/, "Format must be 123-456-7890"),
    otherwise: (schema) => schema.notRequired()
  }),
  
  employer_address_1: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) => schema.required("Employer's Street Address is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  employer_city: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) => schema.required("Employer's City is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  employer_state: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) =>
      schema
        .required("Employer's State is required")
        .matches(/^[A-Z]{2}$/, "Employer's State should be a two-letter uppercase code"),
    otherwise: (schema) => schema.notRequired(),
  }),
  employer_zip: Yup.string().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) =>
      schema
        .required("Employer's Zip Code is required")
        .matches(/^\d{5}$/, "Employer's Zip Code must be exactly 5 digits"),
    otherwise: (schema) => schema.notRequired(),
  }),

  employer_since: Yup.date().when('employment_status', {
    is: (val) => ['full-time', 'part-time','self-employed'].includes(val),
    then: (schema) =>
      schema
        .required('Employer Since is required')
        .typeError('Employer Since must be a valid date')
        .test('is-past', 'Employer Since date cannot be in the future', isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),

  // social_security_income: Yup.string()
  // .default('') // Add this line
  // .when('employment_status', {
  //   is: 'retired',
  //   then: (schema) =>
  //     schema
  //       .required('Social Security Income / Pension is required')
  //       .matches(/^[0-9]+$/, 'Only numbers are allowed in income'),
  //   otherwise: (schema) => schema.notRequired()
  // }),
  
  past_employer: Yup.string().when('employer_since', {
    is: (val) => {
      if (!val) return false;
      const date = new Date(val);
      if (isNaN(date)) return false;
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      return date > twoYearsAgo;
    },
    then: (schema) => schema.required('Previous Employer is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  past_occupation: Yup.string().when('employer_since', {
    is: (val) => {
      if (!val) return false;
      const date = new Date(val);
      if (isNaN(date)) return false;
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      return date > twoYearsAgo;
    },
    then: (schema) => schema.required('Previous Occupation is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  past_hire_date: Yup.date().when('employer_since', {
    is: (val) => {
      if (!val) return false;
      const date = new Date(val);
      if (isNaN(date)) return false;
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      return date > twoYearsAgo;
    },
    then: (schema) =>
      schema
        .required('Previous Hire Date is required')
        .typeError('Must be a valid date'),
    otherwise: (schema) => schema.notRequired()
  }),
  past_last_day_of_employment_date: Yup.date().when('employer_since', {
    is: (val) => {
      if (!val) return false;
      const date = new Date(val);
      if (isNaN(date)) return false;
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      return date > twoYearsAgo;
    },
    then: (schema) =>
      schema
        .required('Last Day of Employment is required')
        .typeError('Must be a valid date'),
    otherwise: (schema) => schema.notRequired()
  }),
  




  coapplicant: Yup.string()
    .required('You must specify if you have a co-borrower')
    .oneOf(['Yes', 'No'], 'Invalid selection for co-borrower'),
  co_borrower_relationship: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required('Relationship to the primary borrower is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_first_name: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required('Co-borrower First Name is required').matches(/^[a-zA-Z\s]*$/, 'First Name should contain only letters and spaces'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_last_name: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required('Co-borrower Last Name is required').matches(/^[a-zA-Z\s]*$/, 'Last Name should contain only letters and spaces'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_phone_1: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Phone Number is required')
        .matches(/^\d{3}-\d{3}-\d{4}$/, 'Co-borrower Phone Number should be 10 digits (e.g., 123-456-7890)'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_email: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Email is required')
        .email('Co-borrower Email must be a valid email'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_dob: Yup.date().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Date of Birth is required')
        .typeError('Co-borrower Date of Birth must be a valid date')
        .test('is-18', 'Co-borrower must be at least 18 years old', isAtLeast18),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_marital_status: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Marital Status is required')
        .oneOf(['Married','Unmarried','Single','Separated'], 'Invalid Co-borrower Marital Status selected'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_drivers_license_type: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower License Type is required')
        .oneOf(
          ['Driving license', 'Temporary Driving License', 'CDL', 'Temporary CDL', 'No Driving License'],
          'Invalid Co-borrower License Type selected'
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_license_no: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required("Co-borrower Driver's License is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_license_state: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower License/ID State is required')
        .matches(/^[A-Z]{2}$/, 'Co-borrower License/ID State should be a two-letter uppercase code'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_drivers_license_issue_date: Yup.date().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower License Issue Date is required')
        .typeError('Co-borrower License Issue Date must be a valid date')
        .test('is-past', 'Co-borrower License Issue Date must be in the past', isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_drivers_license_expire_date: Yup.date().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower License Expiration Date is required')
        .typeError('Co-borrower License Expiration Date must be a valid date')
        .test('is-future', 'Co-borrower License Expiration Date must be in the future', isFutureDate),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_ssn: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower SSN is required')
        .matches(/^\d{3}-\d{2}-\d{4}$/, 'Co-borrower SSN should be 9 digits (e.g., 123-45-6789)'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_ssn_confirm: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Confirm SSN is required')
        .oneOf([Yup.ref('co_borrower_ssn')], 'Co-borrower Confirm SSN does not match SSN'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employment_status: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Employment Status is required')
        .oneOf(['full-time', 'part-time','self-employed', 'un-employed', 'retired'], 'Invalid Co-borrower Employment Status selected'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) => schema.required("Co-borrower's Employer Name is required").matches(/^[a-zA-Z0-9\s-]*$/, 'Employer Name should contain only abc,123,- and spaces'),
    otherwise: (schema) => schema.notRequired(),
  }),


  co_borrower_employer_since: Yup.date().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) =>
      schema
        .required("Co-borrower's Employer Since is required")
        .typeError("Co-borrower's Employer Since must be a valid date")
        .test('is-past', "Co-borrower's Employer Since date cannot be in the future", isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_past_employer: Yup.string().when('co_borrower_employer_since', {
    is: (val) => isLessThan2Years(val),
    then: (schema) => schema.required('Co-borrower Previous Employer is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  co_borrower_past_occupation: Yup.string().when('co_borrower_employer_since', {
    is: (val) => isLessThan2Years(val),
    then: (schema) => schema.required('Co-borrower Previous Occupation is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  co_borrower_past_hire_date: Yup.date().when('co_borrower_employer_since', {
    is: (val) => isLessThan2Years(val),
    then: (schema) =>
      schema
        .required('Co-borrower Previous Hire Date is required')
        .typeError('Co-borrower Previous Hire Date must be a valid date')
        .test('is-past', 'Co-borrower Previous Hire Date cannot be in the future', isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_past_last_day_of_employment_date: Yup.date().when('co_borrower_employer_since', {
    is: (val) => isLessThan2Years(val),
    then: (schema) =>
      schema
        .required('Co-borrower Last Day of Employment is required')
        .typeError('Co-borrower Last Day of Employment must be a valid date')
        .test('is-past', 'Co-borrower Last Day of Employment date cannot be in the future', isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),  


  co_borrower_job_title: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) => schema.required("Co-borrower's Job Title is required").matches(/^[a-zA-Z0-9\s-]*$/, 'Job title should contain only alphabets,letters, numbers, - '),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer_phone_1: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) =>
      schema
        .required("Co-borrower's Employer Phone Number is required")
        .matches(/^\d{3}-\d{3}-\d{4}$/, "Co-borrower's Employer Phone Number should be 10 digits (e.g., 123-456-7890)"),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer_address_1: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) => schema.required("Co-borrower's Employer Street Address is required").matches(/^[a-zA-Z0-9\s-]*$/, 'Address should contain only alphabets, letters, numbers, - '),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer_city: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) => schema.required("Co-borrower's Employer City is required").matches(/^[a-zA-Z0-9\s-]*$/, 'City should contain only letters, numbers, - '),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer_state: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) =>
      schema
        .required("Co-borrower's Employer State is required")
        .matches(/^[A-Z]{2}$/, "Co-borrower's Employer State should be a two-letter uppercase code"),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_employer_zip: Yup.string().when(['coapplicant', 'co_borrower_employment_status'], {
    is: (coapplicant, employmentStatus) => coapplicant === 'Yes' && ['full-time', 'part-time','self-employed'].includes(employmentStatus),
    then: (schema) =>
      schema
        .required("Co-borrower's Employer Zip Code is required")
        .matches(/^\d{5}$/, "Co-borrower's Employer Zip Code must be exactly 5 digits"),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_rent_own: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Rent or Own is required')
        .oneOf(['own', 'rent', 'buying', 'parents', 'others'], 'Invalid Co-borrower house status selected'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_gross_income: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required('Co-borrower Monthly Gross Income is required').matches(/^[0-9]*$/, 'Income should contain only numbers'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_monthly_home_payment: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) => schema.required('Co-borrower Monthly Rent/Mortgage Payment is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_address_1: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Street Address is required')
        .max(100, 'Co-borrower Address should not exceed 100 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
 
  co_borrower_city: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower City is required')
        .max(50, 'Co-borrower City should not exceed 50 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_state: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower State is required')
        .matches(/^[A-Z]{2}$/, 'Co-borrower State should be a two-letter uppercase code'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_zip: Yup.string().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Zip Code is required')
        .matches(/^\d{5}$/, 'Co-borrower Zip Code must be exactly 5 digits'),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_address_since: Yup.date().when('coapplicant', {
    is: 'Yes',
    then: (schema) =>
      schema
        .required('Co-borrower Living Since is required')
        .typeError('Co-borrower Living Since must be a valid date')
        .test('is-past', 'Co-borrower Living Since date cannot be in the future', isPastDate),
    otherwise: (schema) => schema.notRequired(),
  }),
  co_borrower_past_address_1: Yup.string().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) => schema.required('Previous Street Address is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  
  co_borrower_past_city: Yup.string().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) => schema.required('Previous City is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  
  co_borrower_past_state: Yup.string().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) => schema.required('Previous State is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  
  co_borrower_past_zip: Yup.string().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) =>
      schema
        .required('Previous Zip Code is required')
        .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits'),
    otherwise: (schema) => schema.notRequired()
  }),
  
  co_borrower_past_movein_date: Yup.date().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) =>
      schema
        .required('Previous Move-in Date is required')
        .typeError('Invalid date'),
    otherwise: (schema) => schema.notRequired()
  }),
  
  co_borrower_past_moveout_date: Yup.date().when('co_borrower_address_since', {
    is: isLessThan2Years,
    then: (schema) =>
      schema
        .required('Previous Move-out Date is required')
        .typeError('Invalid date'),
    otherwise: (schema) => schema.notRequired()
  }),
  
});